<script setup lang="ts" generic="T extends string | number">
import { computed, ref } from 'vue'
import { Check, Search } from '@lucide/vue'
import PopoverMenu from './PopoverMenu.vue'

const props = withDefaults(
  defineProps<{
    modelValue: T
    options: { value: T; label: string }[]
    label: string
    searchable?: boolean
    disabled?: boolean
  }>(),
  { searchable: false },
)
const emit = defineEmits<{ 'update:modelValue': [value: T] }>()
const search = ref('')
const selected = computed(
  () => props.options.find((option) => option.value === props.modelValue)?.label || '—',
)
const filtered = computed(() =>
  props.options.filter(
    (option) =>
      !search.value ||
      `${option.label} ${option.value}`
        .toLocaleLowerCase()
        .includes(search.value.toLocaleLowerCase()),
  ),
)
function choose(value: T, close: () => void): void {
  emit('update:modelValue', value)
  search.value = ''
  close()
}
</script>

<template>
  <PopoverMenu
    :label="label"
    role="listbox"
    trigger-role="combobox"
    :disabled="disabled"
    :width="260"
    button-class="field flex items-center justify-between gap-3 text-left"
  >
    <template #trigger
      ><span class="truncate">{{ selected }}</span></template
    >
    <template #default="{ close }">
      <div v-if="searchable" class="sticky top-0 mb-1 bg-white p-1 dark:bg-slate-900">
        <label class="relative block"
          ><Search class="absolute left-3 top-3 size-4 text-slate-400" aria-hidden="true" />
          <input
            v-model="search"
            class="field !min-h-10 pl-9"
            :aria-label="label"
            type="search"
            autocomplete="off"
          />
        </label>
      </div>
      <button
        v-for="option in filtered"
        :key="option.value"
        type="button"
        role="option"
        :aria-selected="option.value === modelValue"
        class="menu-item justify-between"
        :class="option.value === modelValue ? 'menu-item-selected' : ''"
        @click="choose(option.value, close)"
      >
        <span>{{ option.label }}</span
        ><Check v-if="option.value === modelValue" class="size-4 shrink-0" aria-hidden="true" />
      </button>
      <p v-if="!filtered.length" class="p-3 text-center text-sm text-slate-400">—</p>
    </template>
  </PopoverMenu>
</template>
